import { queryKeyPrefix } from '@src/corelib/query/utils';
import { useMutation } from '@src/query';


/**
 * Inspection-checklist query key for a part (#277). Mirrors the URL-derived key scheme
 * `['root', ...url.split('/')]` for `/inventory/parts/<seller>/<part>/test-checklist/`.
 */
function testChecklistKey(sellerId, partId) {
  return queryKeyPrefix('inventory', 'parts', sellerId, partId, 'test-checklist', '');
}


/**
 * Part detail key — invalidated on save so the drawer's aggregate reflects the
 * stored checklist. (`Part.tested` is NOT changed by a checklist save; the seller
 * still sets it explicitly — see `inspection_checklist.part_set_test_checklist`.)
 */
function partDetailKey(sellerId, partId) {
  return queryKeyPrefix('inventory', 'parts', sellerId, partId, '');
}


/**
 * Save a part's inspection checklist (#277). Variables: `{ sellerId, partId, checklist }`.
 * PATCHes `{ checklist }`, then invalidates the checklist AND part-detail queries.
 * Follows the injected `{ apiClient, queryClient }` contract.
 *
 * Deliberately separate from `useSaveItemSpecificsMutation` rather than shared: the two
 * differ in endpoint, PATCH body key and — the load-bearing part — which queries they
 * invalidate (specifics invalidates listing-copy, inspection invalidates part detail).
 * The rendering they DO share lives in `components/PartChecklist.jsx`.
 */
function useSaveTestChecklistMutation(params) {
  return useMutation({
    mutateFn: ({
      apiClient, queryClient, sellerId, partId, checklist,
    }) => apiClient
      .request({
        url: `/inventory/parts/${sellerId}/${partId}/test-checklist/`,
        method: 'PATCH',
        payload: { checklist },
      })
      .then((data) => {
        queryClient.invalidateQueries({ queryKey: testChecklistKey(sellerId, partId) });
        queryClient.invalidateQueries({ queryKey: partDetailKey(sellerId, partId) });
        return data;
      }),
    ...params,
  });
}


export { testChecklistKey, useSaveTestChecklistMutation };
